// SO Warehouse Scan — Zebra barcode dispatch scanning, packed against the
// so-warehouse-scan backend. Three vendor types (derived from
// needs_box_mapping/needs_batch_tracking, read server-side from
// so_vendor_scan_requirements and returned by action=detail):
//
//   - plain (neither flag): scan -> qty popup capped at PO qty (soft
//     KAM-mismatch hint, not a hard block) -> card list.
//   - box_only (box mapping, no batch tracking): same card list, packing
//     a SKU is one qty-per-box x number-of-boxes entry (MOQ/case-pack
//     vendors ship in multiples, so packed qty legitimately differs from
//     SO qty — confirmed via a blocking dialog, not silently blocked).
//   - box_batch (both flags): same card list plus a box panel (scan -> a
//     one-time batch/mfg/exp label photo per SKU -> qty popup -> Box Full
//     -> next box), boxes always shown individually.
//
// All three write into so_line_items.actual_qty /
// so_headers.total_actual_qty/boxes_packed/total_weight_kg/
// validation_status/validated_at via save_progress/validate_all — see
// backend/src/functions/so-warehouse-scan.js for the full write shape and
// the kam_validated/already-validated gates enforced there.
//
// Final "Confirm & Save" is gated behind hasEditAccess(user, 'warehouse',
// 'warehouse_access') — same permission domain PoBoxImages.jsx uses.

import { useState, useEffect, useRef, useMemo } from 'react';
import { API_BASE } from '../config';
import { formatDate } from '../utils/formatDate';
import { hasEditAccess } from '../permissionConfig';
import { notifyEditBlocked } from '../components/EditAccessToast';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';

const C = {
    white:     '#ffffff',
    ink:       '#111111',
    inkSoft:   '#2a2a2a',
    red:       '#D0202A',
    redDeep:   '#9E0F17',
    redSoft:   '#FDEAEA',
    green:     '#158A45',
    greenSoft: '#EAFAF0',
    gray:      '#6b6b6b',
    line:      '#e6e6e6',
    panel:     '#f7f7f7',
};

function vibrate() { try { navigator.vibrate?.(40); } catch {} }

function prettyDate(iso) {
    if (!iso) return '';
    const formatted = formatDate(iso);
    return formatted === '—' ? iso : formatted;
}

// po_box_images is keyed by the actual PO number (order_ref_no), not the
// SO voucher number — same sanitization used both when uploading a new
// photo and when looking up already-uploaded ones on resume.
function boxImageKey(header) {
    return String(header?.order_ref_no || header?.voucher_no || '').replace(/[^a-zA-Z0-9]/g, '_');
}

function blobToBase64(blob) {
    return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(String(reader.result).split(',')[1]);
        reader.onerror = reject;
        reader.readAsDataURL(blob);
    });
}

async function readError(res, fallback) {
    try {
        const data = await res.json();
        return data?.error || data?.message || fallback;
    } catch { return fallback; }
}

// ── Pending box-image persistence (IndexedDB) ───────────────────────────────
// A photo that fails to upload (even after runSoImageUpload's 2 automatic
// retries) previously only lived in React state — navigating away from the
// SO or refreshing the tablet lost it silently. IndexedDB can store the raw
// Blob directly (unlike localStorage, which is string-only and has a much
// smaller quota), so a failed photo survives until it actually succeeds.
// ponytail: plain native IndexedDB, no wrapper lib — this is one object
// store with three tiny operations, not worth a dependency.
const PENDING_IMG_DB = 'so_warehouse_scan';
const PENDING_IMG_STORE = 'pending_box_images';

function openPendingImageDB() {
    return new Promise((resolve, reject) => {
        const req = indexedDB.open(PENDING_IMG_DB, 1);
        req.onupgradeneeded = () => {
            const store = req.result.createObjectStore(PENDING_IMG_STORE, { keyPath: 'tempId' });
            store.createIndex('po_number', 'po_number');
        };
        req.onsuccess = () => resolve(req.result);
        req.onerror = () => reject(req.error);
    });
}
async function savePendingImage(tempId, poNumber, blob) {
    try {
        const db = await openPendingImageDB();
        db.transaction(PENDING_IMG_STORE, 'readwrite').objectStore(PENDING_IMG_STORE)
            .put({ tempId, po_number: poNumber, blob, capturedAt: Date.now() });
    } catch (e) { console.error('savePendingImage failed:', e); }
}
async function deletePendingImage(tempId) {
    try {
        const db = await openPendingImageDB();
        db.transaction(PENDING_IMG_STORE, 'readwrite').objectStore(PENDING_IMG_STORE).delete(tempId);
    } catch (e) { console.error('deletePendingImage failed:', e); }
}
function getPendingImagesForPO(poNumber) {
    return openPendingImageDB().then(db => new Promise((resolve, reject) => {
        const req = db.transaction(PENDING_IMG_STORE, 'readonly').objectStore(PENDING_IMG_STORE)
            .index('po_number').getAll(poNumber);
        req.onsuccess = () => resolve(req.result || []);
        req.onerror = () => reject(req.error);
    })).catch(e => { console.error('getPendingImagesForPO failed:', e); return []; });
}

// "MM-YYYY" <-> Date, for the year-then-month batch-label picker — always
// the 1st of the month, these labels never print a day.
function monthYearStrToDate(str) {
    const m = String(str || '').trim().match(/^(\d{1,2})-(\d{4})$/);
    return m ? new Date(Number(m[2]), Number(m[1]) - 1, 1) : null;
}
function dateToMonthYearStr(date) {
    if (!date || isNaN(date)) return '';
    return `${String(date.getMonth() + 1).padStart(2, '0')}-${date.getFullYear()}`;
}
function MonthYearField({ label, value, onChange }) {
    return (
        <LocalizationProvider dateAdapter={AdapterDateFns}>
            <DatePicker
                label={label}
                views={['year', 'month']}
                openTo="year"
                value={monthYearStrToDate(value)}
                onChange={(date) => onChange(dateToMonthYearStr(date))}
                slotProps={{
                    textField: {
                        fullWidth: true,
                        size: 'small',
                        sx: {
                            '& .MuiOutlinedInput-root': { borderRadius: '8px', fontWeight: 700, fontSize: '16px' },
                            '& .MuiOutlinedInput-notchedOutline': { borderColor: C.ink, borderWidth: '2px' },
                        },
                    },
                }}
            />
        </LocalizationProvider>
    );
}

export default function SoWarehouseScan({ user, onExit }) {
    // Pending-SOs list — same shape as SoWarehouseScan.jsx's own list view.
    const [view, setView] = useState('list'); // 'list' | 'so'
    const [soList, setSoList] = useState([]);
    const [search, setSearch] = useState('');
    const [showSuggest, setShowSuggest] = useState(false);
    const [listLoading, setListLoading] = useState(true);
    const searchRef = useRef(null);

    const [header, setHeader]   = useState(null);
    const [lines, setLines]     = useState([]);
    const [needsBoxMapping, setNeedsBoxMapping]       = useState(false);
    const [needsBatchTracking, setNeedsBatchTracking] = useState(false);
    const [loadError, setLoadError] = useState('');
    const [lockedGate, setLockedGate] = useState(null); // { code, message } | null

    // Type-1 (plain) — mirrors SoWarehouseScan.jsx's scannedQtys exactly.
    const [scannedQtys, setScannedQtys] = useState({}); // line_id -> qty

    // Box types (2/3) — boxes: [{ number, items:[{line_id, ean, sku_name, qty}], sealed }]
    const [boxes, setBoxes] = useState([]);
    const [savingBox, setSavingBox] = useState(false);
    const nextBoxNumberRef = useRef(1); // always MAX(existing box_number)+1 — never renumbered/reused
    // Set for the duration of any box-mutating request (seal/delete/edit/
    // box_only redo) — blocks a second concurrent mutation for this SO
    // (e.g. a slow first save + an impatient retry) from ever starting,
    // which is what corrupted box numbering in production before this
    // guard existed (two overlapping box_only redos raced on the same
    // box-number bookkeeping and produced duplicate box_number rows).
    const boxWriteInFlightRef = useRef(false);

    // Editing a sealed single-SKU box's qty — one atomic update_box_qty call.
    // Type-3 box strip — which box's full details are shown in the large
    // panel below the scrollable icon row. null = defaults to whichever
    // box is currently open/scanning.
    const [viewedBoxNumber, setViewedBoxNumber] = useState(null);
    const [editingBox, setEditingBox] = useState(null); // box number
    const [editQtyInput, setEditQtyInput] = useState('');
    const [editError, setEditError] = useState('');

    // Unified scan popup (Type-1 & Type-3): unknown EAN / pick-a-line (SKU
    // on multiple lines) / scan qty entry — same shape as production's `popup`.
    const [popup, setPopup] = useState(null);
    const [qtyInput, setQtyInput] = useState('');
    const [qtyError, setQtyError] = useState('');
    const qtyInputRef = useRef(null);

    // Type-3 box-qty popup (going into the currently-open box) — distinct
    // shape from the Type-1 popup above since it needs box context.
    const [boxQtyPopup, setBoxQtyPopup] = useState(null); // { line }
    const [boxQtyInput, setBoxQtyInput] = useState('');
    const [boxQtyError, setBoxQtyError] = useState('');

    // Type-2 (box_only) — qty-per-box x number-of-boxes entry.
    const [boxOnlyEntry, setBoxOnlyEntry] = useState(null);
    const [boxOnlyPerBox, setBoxOnlyPerBox] = useState('');
    const [boxOnlyNumBoxes, setBoxOnlyNumBoxes] = useState('');
    const [boxOnlyError, setBoxOnlyError] = useState('');
    const [boxOnlyConfirm, setBoxOnlyConfirm] = useState(null); // { perBox, numBoxes, packedQty, diff }
    const [boxOnlyBusy, setBoxOnlyBusy] = useState(false);

    // Batch label photo flow (Type-3 only)
    const [batchLine, setBatchLine] = useState(null);
    const [batchExtracting, setBatchExtracting] = useState(false);
    const [batchConfirm, setBatchConfirm] = useState(null); // { batch_no, mfg_date, exp_date }
    const [batchError, setBatchError] = useState('');
    const [batchDiag, setBatchDiag] = useState(null);
    // True when the confirm form was opened via the ✏️ edit icon on an
    // already-saved line (correcting a mistake), not the normal first-scan
    // photo flow — determines which backend action confirmBatchInfo calls.
    const [isEditingBatch, setIsEditingBatch] = useState(false);

    // SO-level box-image gallery (optional, not tied to any specific box).
    const [soImages, setSoImages] = useState([]);
    const [galleryModalOpen, setGalleryModalOpen] = useState(false);
    const [expandedImage, setExpandedImage] = useState(null);

    // Shared camera (SO gallery photo / batch label photo)
    const [camera, setCamera] = useState(null); // { mode: 'gallery'|'batch' }
    const [previewZoom, setPreviewZoom] = useState(1);
    const videoRef  = useRef(null);
    const streamRef = useRef(null);

    // Final Check review sequence — same shape as production's
    // boxCountPopup -> finalCheckOpen -> successPopup.
    const [boxCountPopup, setBoxCountPopup] = useState(false);
    const [boxCount, setBoxCount] = useState('');
    const [boxCountError, setBoxCountError] = useState('');
    const [totalWeight, setTotalWeight] = useState('');
    const [totalWeightError, setTotalWeightError] = useState('');
    const [finalCheckOpen, setFinalCheckOpen] = useState(false);
    const [successPopup, setSuccessPopup] = useState(false);
    const [saving, setSaving] = useState(false);

    const scanInputRef = useRef(null);
    const scanDebounce = useRef(null);

    useEffect(() => () => streamRef.current?.getTracks().forEach(t => t.stop()), []);

    // ── Vendor type ──────────────────────────────────────────────────────────
    const vendorType = !needsBoxMapping ? 'plain' : (needsBatchTracking ? 'box_batch' : 'box_only');

    // ── Pending-SOs list ─────────────────────────────────────────────────────
    async function loadList() {
        setListLoading(true);
        try {
            const res  = await fetch(`${API_BASE}/so-warehouse-scan?action=pending`);
            const data = await res.json();
            setSoList(Array.isArray(data) ? data : []);
        } catch (e) { console.error(e); }
        finally { setListLoading(false); }
    }
    useEffect(() => { loadList(); }, []);

    const filteredList = useMemo(() => {
        const q = search.trim().toLowerCase();
        if (!q) return soList;
        return soList.filter(so =>
            String(so.voucher_no ?? '').toLowerCase().includes(q) ||
            String(so.buyer ?? '').toLowerCase().includes(q) ||
            String(so.consignee ?? '').toLowerCase().includes(q)
        );
    }, [soList, search]);
    const suggestions = useMemo(() => filteredList.slice(0, 5), [filteredList]);

    function highlight(text, q) {
        if (!q) return text;
        const idx = String(text).toLowerCase().indexOf(q.toLowerCase());
        if (idx === -1) return text;
        return (<>{text.slice(0, idx)}<b style={{ color: C.red }}>{text.slice(idx, idx + q.length)}</b>{text.slice(idx + q.length)}</>);
    }

    // ── Load real SO ────────────────────────────────────────────────────────
    async function loadSO(so) {
        setLoadError(''); setLockedGate(null);
        setView('so');
        try {
            const res  = await fetch(`${API_BASE}/so-warehouse-scan?action=detail&voucher_no=${encodeURIComponent(so.voucher_no)}`);
            const data = await res.json();
            if (res.status === 403 && data?.gate) { setLockedGate({ code: data.gate, message: data.message }); return; }
            if (!res.ok) { setLoadError(data?.error || 'Failed to load SO'); setView('list'); return; }

            setHeader(data.header);
            setLines(data.lines || []);
            setNeedsBoxMapping(!!data.needs_box_mapping);
            setNeedsBatchTracking(!!data.needs_batch_tracking);

            const resumedScanned = {};
            (data.lines || []).forEach(l => { if (l.actual_qty !== null && l.actual_qty !== undefined) resumedScanned[l.id] = l.actual_qty; });
            setScannedQtys(resumedScanned);

            const resumed = (data.boxes || []).map(b => ({ number: b.box_number, items: b.items, sealed: true }));
            resumed.push({ number: (resumed.reduce((m, b) => Math.max(m, b.number), 0)) + 1, items: [], sealed: false });
            setBoxes(resumed);
            nextBoxNumberRef.current = resumed[resumed.length - 1].number;

            const poNum = boxImageKey(data.header);
            try {
                const imgRes  = await fetch(`${API_BASE}/wh-po-images-get?po_number=${encodeURIComponent(poNum)}`);
                const imgData = await imgRes.json();
                const serverImages = (imgData.images || []).map(img => ({ id: img.id, blobName: img.blob_name, dataUrl: img.view_url }));
                // Restore any photo that failed to upload on a previous visit
                // to this SO (tablet refreshed/navigated away before it could
                // be manually retried) — otherwise it'd be lost for good the
                // moment this reload replaces soImages with the server's list.
                const pending = await getPendingImagesForPO(poNum);
                const restoredFailed = pending.map(p => ({
                    tempId: p.tempId, dataUrl: URL.createObjectURL(p.blob), blob: p.blob, failed: true,
                }));
                setSoImages([...serverImages, ...restoredFailed]);
            } catch (imgErr) { console.error('Failed to load existing box images:', imgErr); }
        } catch (e) { console.error(e); setLoadError('Network error loading SO'); setView('list'); }
    }

    // ── Scanning ────────────────────────────────────────────────────────────
    function focusScan() {
        if (header && !lockedGate && !popup && !boxQtyPopup && !boxOnlyEntry && !finalCheckOpen && !boxCountPopup && !camera && !batchConfirm && !batchLine && scanInputRef.current) {
            scanInputRef.current.focus({ preventScroll: true });
        }
    }
    useEffect(() => {
        focusScan();
        const t = setInterval(focusScan, 700);
        return () => clearInterval(t);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [header, lockedGate, popup, boxQtyPopup, boxOnlyEntry, finalCheckOpen, boxCountPopup, camera, batchConfirm, batchLine]);

    function finishScan(raw) {
        const code = String(raw).trim();
        clearTimeout(scanDebounce.current);
        if (scanInputRef.current) scanInputRef.current.value = '';
        if (code.length > 3) handleScan(code);
    }

    // Deliberately collects ALL matching lines, not just the first — the
    // same SKU can legitimately appear on several line items of one SO,
    // and a `.find()` here would silently make the other lines unscannable
    // (this was a real, previously-fixed production bug — see the "pick"
    // popup below).
    function handleScan(eanOrCode) {
        const scanned = eanOrCode?.trim();
        const matches = lines.filter(l => l.ean === eanOrCode || l.ean?.trim() === scanned);
        if (matches.length === 0) {
            setPopup({ type: 'unknown', ean: eanOrCode, voucherNo: header?.voucher_no });
            return;
        }
        if (matches.length === 1) { routeScan(matches[0]); return; }
        setPopup({ type: 'pick', ean: matches[0].ean, skuName: matches[0].sku_name, matches });
    }

    function routeScan(line) {
        if (boxWriteInFlightRef.current) return;
        vibrate();
        if (vendorType === 'plain') { openLinePopup(line); return; }
        if (vendorType === 'box_only') { openBoxOnlyEntry(line); return; }
        scanIntoBox(line);
    }

    // ── Derived ─────────────────────────────────────────────────────────────
    const refQty = (l) => l.kam_validated_qty ?? l.po_qty; // display/mismatch reference only
    const packedByLine = {};
    boxes.forEach(b => b.items.forEach(it => { packedByLine[it.line_id] = (packedByLine[it.line_id] || 0) + Number(it.qty); }));
    // Hard cap is always PO qty, matching production — a KAM-qty mismatch
    // is a soft warning shown in the popups, never a block below PO qty.
    const remainingBox = (l) => l.po_qty - (packedByLine[l.id] || 0);

    function dispatchQtyFor(l) {
        return vendorType === 'plain' ? scannedQtys[l.id] : (packedByLine[l.id] || 0);
    }
    function isLineDone(l) {
        return vendorType === 'plain' ? scannedQtys[l.id] !== undefined : (packedByLine[l.id] || 0) > 0;
    }
    const doneLines      = lines.filter(isLineDone);
    const scannedCount   = doneLines.length;
    const sealedCount    = boxes.filter(b => b.sealed).length;
    const activeBoxIdx   = boxes.findIndex(b => !b.sealed);
    const activeBox      = boxes[activeBoxIdx];

    // ── Type-1 (plain) qty popup — same shape as SoWarehouseScan.jsx ────────
    function openLinePopup(l) {
        const scanned = scannedQtys[l.id] !== undefined;
        setQtyError('');
        setQtyInput(scanned ? String(scannedQtys[l.id]) : String(l.kam_validated_qty ?? l.po_qty ?? ''));
        setPopup({ type: scanned ? 'rescan' : 'scan', lineId: l.id, ean: l.ean, skuName: l.sku_name, kamQty: l.kam_validated_qty, poQty: l.po_qty, existing: scanned ? scannedQtys[l.id] : null });
        setTimeout(() => qtyInputRef.current?.focus(), 100);
    }
    function savePopupQty() {
        const qty = parseFloat(qtyInput);
        if (isNaN(qty) || qty < 0) { setQtyError('Enter a valid quantity'); return; }
        if (qty > popup.poQty) { setQtyError(`Dispatch qty can't exceed PO qty (${popup.poQty})`); return; }
        const savedLineId = popup.lineId;
        setScannedQtys(prev => ({ ...prev, [savedLineId]: qty }));
        setPopup(null);
        setQtyInput(''); setQtyError('');
        setTimeout(focusScan, 50);
    }

    // Persist Type-1 progress without finalizing — used both by the
    // explicit "Save Progress & Exit" button and (best-effort) on leaving.
    async function persistProgress() {
        if (!header || scannedCount === 0) return;
        const line_quantities = lines.filter(l => scannedQtys[l.id] !== undefined).map(l => ({ id: l.id, qty: scannedQtys[l.id] }));
        try {
            await fetch(`${API_BASE}/so-warehouse-scan?action=save_progress`, {
                method: 'PUT', headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ voucher_no: header.voucher_no, line_quantities }),
            });
        } catch (e) { console.error('save_progress failed:', e); }
    }
    async function saveProgressAndExit() {
        if (scannedCount === 0) return;
        setSaving(true);
        try { await persistProgress(); resetToLoadScreen(); }
        finally { setSaving(false); }
    }

    // ── Camera (shared: SO gallery photo / batch label) ─────────────────────
    async function openCamera(mode) {
        try {
            streamRef.current = await navigator.mediaDevices.getUserMedia({
                video: { facingMode: { ideal: 'environment' }, width: { ideal: 3840 }, height: { ideal: 2160 } },
                audio: false,
            });
            setCamera({ mode });
        } catch (err) {
            console.error('getUserMedia failed:', err);
            alert('Camera access is required for this step.');
        }
    }
    function closeCamera() {
        streamRef.current?.getTracks().forEach(t => t.stop());
        streamRef.current = null;
        setCamera(null);
    }
    function attachVideo(el) {
        videoRef.current = el;
        if (el && streamRef.current && el.srcObject !== streamRef.current) {
            el.srcObject = streamRef.current;
            el.play().catch(e => console.error(e));
        }
    }
    async function grabHighResSource() {
        const track = streamRef.current?.getVideoTracks?.()[0];
        if (track && typeof window.ImageCapture === 'function') {
            try {
                const capture = new window.ImageCapture(track);
                const blob = await capture.takePhoto();
                const img = await new Promise((resolve, reject) => {
                    const el = new Image();
                    el.onload = () => resolve(el);
                    el.onerror = reject;
                    el.src = URL.createObjectURL(blob);
                });
                return { el: img, width: img.naturalWidth, height: img.naturalHeight, via: 'ImageCapture' };
            } catch (err) { console.error('ImageCapture.takePhoto() failed, falling back to video frame:', err); }
        }
        const v = videoRef.current;
        if (!v?.videoWidth) return null;
        return { el: v, width: v.videoWidth, height: v.videoHeight, via: 'video-frame' };
    }
    function adaptiveZoomFor(source, targetLongEdge, maxZoom) {
        const nativeLongEdge = Math.max(source.width, source.height);
        return Math.min(maxZoom, Math.max(1, nativeLongEdge / targetLongEdge));
    }
    async function cropSource(source, zoom, format, quality) {
        const sw = source.width / zoom, sh = source.height / zoom;
        const sx = (source.width - sw) / 2, sy = (source.height - sh) / 2;
        const cv = document.createElement('canvas');
        cv.width = sw; cv.height = sh;
        cv.getContext('2d').drawImage(source.el, sx, sy, sw, sh, 0, 0, sw, sh);
        const blob = await new Promise((resolve) => cv.toBlob(resolve, format, quality));
        return { blob, sourceWidth: source.width, sourceHeight: source.height, cropWidth: Math.round(sw), cropHeight: Math.round(sh), via: source.via };
    }
    async function captureFrame(zoom = 1, format = 'image/jpeg', quality = 0.95) {
        const source = await grabHighResSource();
        if (!source) return null;
        return cropSource(source, zoom, format, quality);
    }
    const BATCH_LABEL_TARGET_LONG_EDGE = 2000;
    const BATCH_LABEL_MAX_ZOOM = 5;
    async function captureBatchLabelFrame() {
        const source = await grabHighResSource();
        if (!source) return null;
        const zoom = adaptiveZoomFor(source, BATCH_LABEL_TARGET_LONG_EDGE, BATCH_LABEL_MAX_ZOOM);
        return cropSource(source, zoom, 'image/png', 1);
    }

    // ── Type-3 batch label photo -> OCR -> confirm -> save ──────────────────
    async function captureBatchLabel() {
        const captured = await captureBatchLabelFrame();
        closeCamera();
        if (!captured) return;
        setBatchDiag(captured);
        setBatchExtracting(true);
        try {
            const base64 = await blobToBase64(captured.blob);
            const res  = await fetch(`${API_BASE}/so-warehouse-scan?action=extract_batch_photo`, {
                method: 'PUT', headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ image_base64: base64, mime_type: captured.blob.type }),
            });
            const data = await res.json();
            if (!res.ok) { setBatchError(await readError(res, 'OCR failed — retake photo')); return; }
            setBatchConfirm({ batch_no: data.batch_no || '', mfg_date: data.mfg_date || '', exp_date: data.exp_date || '' });
        } catch (e) { console.error(e); setBatchError('OCR request failed — retake photo'); }
        finally { setBatchExtracting(false); }
    }
    async function confirmBatchInfo() {
        const action = isEditingBatch ? 'update_batch_info' : 'save_batch_info';
        try {
            await fetch(`${API_BASE}/so-warehouse-scan?action=${action}`, {
                method: 'PUT', headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ line_id: batchLine.id, ...batchConfirm }),
            });
            setLines(prev => prev.map(l => l.id === batchLine.id ? { ...l, ...batchConfirm } : l));
        } catch (e) { console.error(`${action} failed:`, e); }
        const line = batchLine;
        const wasEditing = isEditingBatch;
        setBatchConfirm(null); setBatchLine(null); setIsEditingBatch(false);
        if (!wasEditing) openBoxQtyPopup(line);
    }

    // Opens the same confirm form used after a photo, pre-filled with the
    // line's currently-saved batch/mfg/exp — no new photo needed, just a
    // direct correction.
    function editBatchInfo(line) {
        setIsEditingBatch(true);
        setBatchLine(line);
        setBatchConfirm({ batch_no: line.batch_no || '', mfg_date: line.mfg_date || '', exp_date: line.exp_date || '' });
        setBatchError('');
    }

    // ── Type-3 scan -> (one-time batch photo) -> box qty popup -> Box Full ──
    function scanIntoBox(line) {
        if (needsBatchTracking && !(line.batch_no && line.mfg_date && line.exp_date)) {
            setBatchLine(line); setBatchError('');
            return;
        }
        openBoxQtyPopup(line);
    }
    function openBoxQtyPopup(line) { setBoxQtyInput(''); setBoxQtyError(''); setBoxQtyPopup(line); }
    function saveBoxQty() {
        const qty = parseInt(boxQtyInput, 10);
        if (isNaN(qty) || qty <= 0) { setBoxQtyError('Enter a valid quantity'); return; }
        if (qty > remainingBox(boxQtyPopup)) { setBoxQtyError(`Only ${remainingBox(boxQtyPopup)} remaining (PO qty)`); return; }
        const line = boxQtyPopup;
        setBoxes(prev => prev.map((b, i) => {
            if (i !== activeBoxIdx) return b;
            const existing = b.items.find(it => it.line_id === line.id);
            const items = existing
                ? b.items.map(it => it.line_id === line.id ? { ...it, qty: it.qty + qty } : it)
                : [...b.items, { line_id: line.id, sku_name: line.sku_name, ean: line.ean, qty }];
            return { ...b, items };
        }));
        setBoxQtyPopup(null);
    }
    async function boxFullTapped() {
        if (!activeBox || activeBox.items.length === 0) return;
        await sealBoxNow(activeBox);
    }
    async function sealBoxNow(box) {
        setSavingBox(true);
        boxWriteInFlightRef.current = true;
        try {
            const res = await fetch(`${API_BASE}/so-warehouse-scan?action=save_box`, {
                method: 'PUT', headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ voucher_no: header.voucher_no, box_number: box.number, items: box.items }),
            });
            if (!res.ok) { alert(await readError(res, 'Failed to save box')); return; }
            setBoxes(prev => {
                const next = prev.map(b => b.number === box.number ? { ...b, items: box.items, sealed: true } : b);
                next.push({ number: box.number + 1, items: [], sealed: false });
                return next;
            });
            nextBoxNumberRef.current = box.number + 1;
            setViewedBoxNumber(null); // back to defaulting to the (new) open box
        } catch (e) { console.error(e); alert('Failed to save box — check connection'); }
        finally { setSavingBox(false); boxWriteInFlightRef.current = false; }
    }
    // No client-side renumbering — a deleted box just leaves a gap; box
    // numbers are never reused or shifted (a confirmed prior bug: renumbering
    // locally without a matching server-side renumber could merge two
    // distinct boxes under one box_number on a later save).
    // The backend shifts every box numbered higher than the deleted one
    // down by 1, atomically, in the same request (sequential, gap-free
    // numbering). This mirrors that EXACT same shift locally — not
    // independent renumbering logic — so client and server never disagree
    // about what a given box_number means.
    async function deleteBox(boxNumber, { silent } = {}) {
        boxWriteInFlightRef.current = true;
        try {
            const res = await fetch(`${API_BASE}/so-warehouse-scan?action=delete_box`, {
                method: 'PUT', headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ voucher_no: header.voucher_no, box_number: boxNumber }),
            });
            if (!res.ok) { if (!silent) alert(await readError(res, 'Failed to delete box')); return false; }
        } catch (e) { console.error('delete_box failed:', e); if (!silent) alert('Failed to delete box — check connection'); return false; }
        finally { boxWriteInFlightRef.current = false; }
        setBoxes(prev => {
            const next = prev
                .filter(b => b.number !== boxNumber)
                .map(b => b.number > boxNumber ? { ...b, number: b.number - 1 } : b);
            const openBox = next.find(b => !b.sealed);
            nextBoxNumberRef.current = openBox ? openBox.number : Math.max(0, ...next.map(b => b.number)) + 1;
            return next;
        });
        setViewedBoxNumber(null); // numbers just shifted — drop any stale selection
        return true;
    }
    function startEditBox(box) { setEditingBox(box.number); setEditQtyInput(String(box.items[0].qty)); setEditError(''); }
    function cancelEditBox() { setEditingBox(null); setEditError(''); }
    async function editBoxQty(box) {
        const newQty = parseInt(editQtyInput, 10);
        if (isNaN(newQty) || newQty <= 0) { setEditError('Enter a valid quantity'); return; }
        if (newQty === box.items[0].qty) { setEditingBox(null); return; }
        setSavingBox(true);
        boxWriteInFlightRef.current = true;
        try {
            const res = await fetch(`${API_BASE}/so-warehouse-scan?action=update_box_qty`, {
                method: 'PUT', headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ voucher_no: header.voucher_no, box_number: box.number, line_id: box.items[0].line_id, qty: newQty }),
            });
            if (!res.ok) { setEditError(await readError(res, 'Failed to save')); return; }
            setBoxes(prev => prev.map(b => b.number === box.number ? { ...b, items: b.items.map(it => ({ ...it, qty: newQty })) } : b));
            setEditingBox(null);
        } catch (e) { console.error('update_box_qty failed:', e); setEditError('Network error — try again'); }
        finally { setSavingBox(false); boxWriteInFlightRef.current = false; }
    }

    // ── Type-2 (box_only) — qty-per-box x number-of-boxes ────────────────────
    function boxOnlyExistingBoxes(line) {
        return boxes.filter(b => b.sealed && b.items.length === 1 && b.items[0].line_id === line.id).sort((a, b) => a.number - b.number);
    }
    function openBoxOnlyEntry(line) {
        const existing = boxOnlyExistingBoxes(line);
        setBoxOnlyPerBox(existing.length ? String(existing[0].items[0].qty) : '');
        setBoxOnlyNumBoxes(existing.length ? String(existing.length) : '');
        setBoxOnlyError(''); setBoxOnlyConfirm(null);
        setBoxOnlyEntry(line);
    }
    function closeBoxOnlyEntry() { setBoxOnlyEntry(null); setBoxOnlyConfirm(null); setBoxOnlyError(''); }
    function attemptSaveBoxOnly() {
        if (boxOnlyBusy || boxWriteInFlightRef.current) return; // guards the Enter-key path too, not just the disabled Save button
        const perBox = parseInt(boxOnlyPerBox, 10), numBoxes = parseInt(boxOnlyNumBoxes, 10);
        if (isNaN(perBox) || perBox <= 0) { setBoxOnlyError('Enter a valid qty per box'); return; }
        if (isNaN(numBoxes) || numBoxes <= 0) { setBoxOnlyError('Enter a valid number of boxes'); return; }
        setBoxOnlyError('');
        const packedQty = perBox * numBoxes;
        const so = refQty(boxOnlyEntry);
        if (packedQty !== so) setBoxOnlyConfirm({ perBox, numBoxes, packedQty, diff: packedQty - so });
        else commitBoxOnly(perBox, numBoxes);
    }
    // Single atomic backend call — replaces every existing box_only box for
    // ALL lines with a from-scratch waterfall renumbering (SKU 1's boxes
    // always start at 1, SKU 2's right after, etc., regardless of packing
    // order), server-side, in one transaction. One round trip total instead
    // of a purge-then-recreate loop of many sequential requests — both the
    // performance fix ("not able to update quickly") and, combined with the
    // boxWriteInFlightRef guard, the correctness fix (a slow redo used to
    // invite a retry that raced the first attempt and produced duplicate
    // box_number rows in production).
    async function commitBoxOnly(perBox, numBoxes) {
        if (boxWriteInFlightRef.current) return;
        setBoxOnlyBusy(true);
        boxWriteInFlightRef.current = true;
        try {
            const line = boxOnlyEntry;
            const res = await fetch(`${API_BASE}/so-warehouse-scan?action=save_box_only_line`, {
                method: 'PUT', headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ voucher_no: header.voucher_no, line_id: line.id, ean: line.ean, sku_name: line.sku_name, per_box: perBox, num_boxes: numBoxes }),
            });
            if (!res.ok) { setBoxOnlyError(await readError(res, 'Failed to save')); return; }
            const data = await res.json();
            setBoxes(data.boxes.map(b => ({ ...b, sealed: true })));
            closeBoxOnlyEntry();
        } catch (e) { console.error('save_box_only_line failed:', e); setBoxOnlyError('Network error — try again'); }
        finally { setBoxOnlyBusy(false); boxWriteInFlightRef.current = false; }
    }
    function boxOnlySummaryFor(line) {
        const existing = boxOnlyExistingBoxes(line);
        if (!existing.length) return null;
        const first = existing[0].number, last = existing[existing.length - 1].number;
        const perBox = existing[0].items[0].qty;
        const uniform = existing.every(b => b.items[0].qty === perBox);
        const packedQty = existing.reduce((s, b) => s + Number(b.items[0].qty), 0);
        return { count: existing.length, first, last, perBox: uniform ? perBox : null, packedQty, mismatch: packedQty !== refQty(line) };
    }

    // ── SO-level box-image gallery ───────────────────────────────────────────
    // 2 automatic retries (short backoff) before surfacing a failure — most
    // upload failures on warehouse wifi are transient blips that self-heal;
    // only a persistently-failing upload should ever need a human to notice.
    // Previously any failure (network error or non-success response) just
    // silently removed the photo from view with no indication it was lost.
    const IMAGE_RETRY_DELAYS_MS = [1000, 2000];
    function sleep(ms) { return new Promise(r => setTimeout(r, ms)); }

    async function runSoImageUpload(tempId, dataUrl, blob) {
        for (let attempt = 0; attempt <= IMAGE_RETRY_DELAYS_MS.length; attempt++) {
            try {
                const poNum = boxImageKey(header);
                const fd = new FormData();
                fd.append('po_number', poNum);
                fd.append('uploaded_by', user?.display_name || user?.username || 'Warehouse');
                fd.append('images', new File([blob], `${poNum}_img_${Date.now()}.jpg`, { type: 'image/jpeg' }));
                const res  = await fetch(`${API_BASE}/wh-po-images-upload`, { method: 'POST', body: fd });
                const data = await res.json();
                const uploaded = data?.images?.[0];
                if (!res.ok || !data.success || !uploaded) throw new Error(data?.error || data?.message || 'Upload failed');
                setSoImages(prev => prev.map(img => img.tempId === tempId ? { id: uploaded.id, blobName: uploaded.blob_name, dataUrl } : img));
                deletePendingImage(tempId); // safely on the server now — no need to keep a local copy
                return true;
            } catch (e) {
                console.error(`SO image upload attempt ${attempt + 1} failed:`, e);
                if (attempt < IMAGE_RETRY_DELAYS_MS.length) { await sleep(IMAGE_RETRY_DELAYS_MS[attempt]); continue; }
                // All automatic retries exhausted — keep it visible with a
                // manual-retry affordance instead of silently discarding it.
                setSoImages(prev => prev.map(img => img.tempId === tempId ? { ...img, blob, uploading: false, failed: true } : img));
                return false;
            }
        }
    }
    function uploadSoImage(blob) {
        const dataUrl = URL.createObjectURL(blob);
        setSoImages(prev => [...prev, { tempId: dataUrl, dataUrl, blob, uploading: true }]);
        savePendingImage(dataUrl, boxImageKey(header), blob); // survives a nav-away/refresh until it succeeds
        runSoImageUpload(dataUrl, dataUrl, blob);
    }
    function retrySoImage(img) {
        setSoImages(prev => prev.map(i => i.tempId === img.tempId ? { ...i, uploading: true, failed: false } : i));
        runSoImageUpload(img.tempId, img.dataUrl, img.blob);
    }
    async function captureSoImage() {
        const captured = await captureFrame();
        if (!captured) return;
        vibrate();
        uploadSoImage(captured.blob);
    }
    async function removeSoImage(idx) {
        const img = soImages[idx];
        if (img?.id) {
            try { await fetch(`${API_BASE}/wh-po-images-upload?id=${img.id}`, { method: 'DELETE' }); }
            catch (e) { console.error('Failed to delete SO image:', e); return; }
        }
        setSoImages(prev => prev.filter((_, i) => i !== idx));
    }

    // ── Final Check review sequence ──────────────────────────────────────────
    function confirmBoxCount() {
        if (vendorType === 'plain') {
            const n = parseInt(boxCount, 10);
            if (isNaN(n) || n < 0) { setBoxCountError('Enter a valid number of boxes'); return; }
        }
        const w = parseFloat(totalWeight);
        if (isNaN(w) || w < 0) { setTotalWeightError('Enter a valid weight'); return; }
        setBoxCountPopup(false);
        setFinalCheckOpen(true);
    }
    async function finalSave() {
        if (!header) return;
        setSaving(true);
        try {
            // Last-chance retry for any photo that's still sitting in a failed
            // state — this is often minutes after capture (the packer scanning
            // more lines in between), by which point a transient wifi blip has
            // usually cleared. Doesn't block the actual save on the outcome by
            // itself; just warns before continuing, since resetToLoadScreen()
            // wipes soImages and a still-failed photo would be lost for good.
            const stillFailing = soImages.filter(img => img.failed);
            if (stillFailing.length > 0) {
                const results = await Promise.all(stillFailing.map(img => runSoImageUpload(img.tempId, img.dataUrl, img.blob)));
                const failedCount = results.filter(ok => !ok).length;
                if (failedCount > 0) {
                    const proceed = window.confirm(`${failedCount} photo${failedCount > 1 ? 's' : ''} still failed to upload and will be lost if you continue. Save anyway?`);
                    if (!proceed) { setSaving(false); return; }
                }
            }
            const line_quantities = vendorType === 'plain'
                ? lines.map(l => ({ id: l.id, qty: scannedQtys[l.id] !== undefined ? scannedQtys[l.id] : 0 }))
                : undefined;
            const res = await fetch(`${API_BASE}/so-warehouse-scan?action=validate_all`, {
                method: 'PUT', headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    voucher_no: header.voucher_no,
                    line_quantities,
                    boxes_packed: vendorType === 'plain' ? (parseInt(boxCount, 10) || 0) : sealedCount,
                    total_weight_kg: parseFloat(totalWeight) || 0,
                }),
            });
            const data = await res.json();
            if (res.ok && data.success) { setFinalCheckOpen(false); setSuccessPopup(true); }
            else alert(data?.error || 'Failed to save');
        } catch (e) { console.error(e); alert('Network error — failed to save'); }
        finally { setSaving(false); }
    }
    function resetToLoadScreen() {
        setView('list');
        setHeader(null); setLines([]); setLockedGate(null);
        setScannedQtys({}); setBoxes([]);
        setBoxCountPopup(false); setBoxCount(''); setBoxCountError('');
        setTotalWeight(''); setTotalWeightError('');
        setFinalCheckOpen(false); setSuccessPopup(false);
        setSoImages([]);
        closeCamera();
        loadList();
    }

    const S = {
        page:    { minHeight: '100vh', background: C.white, color: C.ink, fontFamily: '-apple-system, BlinkMacSystemFont, sans-serif', maxWidth: '430px', margin: '0 auto', padding: '0' },
        header:  { background: C.ink, padding: '16px 18px', position: 'sticky', top: 0, zIndex: 100 },
        body:    { padding: '14px' },
        card:    { background: C.white, border: `1.5px solid ${C.ink}`, borderRadius: '10px', marginBottom: '12px', overflow: 'hidden' },
        priBtn:  { width: '100%', padding: '16px', background: C.red, color: '#fff', border: 'none', borderRadius: '10px', fontSize: '16px', fontWeight: 700, cursor: 'pointer', marginTop: '8px' },
        secBtn:  { padding: '10px 20px', background: '#eee', color: '#333', border: 'none', borderRadius: '8px', fontSize: '14px', fontWeight: 600, cursor: 'pointer' },
        overlay: { position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.85)', zIndex: 200, display: 'flex', alignItems: 'flex-end', justifyContent: 'center' },
        modal:   { background: C.white, borderRadius: '16px 16px 0 0', width: '100%', maxWidth: '430px', padding: '22px 20px', maxHeight: '85vh', overflowY: 'auto', border: `2px solid ${C.ink}`, borderBottom: 'none' },
        input:   { width: '100%', padding: '12px', background: '#222', border: '1px solid #333', borderRadius: '8px', color: '#fff', fontSize: '14px', outline: 'none' },
        refreshBtn: { position: 'absolute', top: '14px', right: '16px', width: '32px', height: '32px', borderRadius: '8px', border: '1px solid #333', background: '#222', color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer' },
    };

    // ── PENDING-SOs LIST ─────────────────────────────────────────────────
    if (view === 'list' && !lockedGate) return (
        <div style={S.page}>
            <div style={{ ...S.header, position: 'relative' }}>
                <button onClick={loadList} title="Refresh" disabled={listLoading} style={S.refreshBtn}>
                    <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                        <polyline points="23 4 23 10 17 10" />
                        <polyline points="1 20 1 14 7 14" />
                        <path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15" />
                    </svg>
                </button>
                <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '4px' }}>
                    {onExit && <button onClick={onExit} style={{ background: 'none', border: 'none', color: '#fff', fontSize: '20px', cursor: 'pointer', padding: 0 }}>←</button>}
                </div>
                <div style={{ fontSize: '21px', fontWeight: 800, marginBottom: '12px', color: '#fff' }}>Box &amp; Batch Scan</div>
                <div style={{ position: 'relative' }}>
                    <input ref={searchRef} type="text" placeholder="Search by SO number or party..."
                        value={search}
                        onFocus={() => setShowSuggest(true)}
                        onBlur={() => setTimeout(() => setShowSuggest(false), 150)}
                        onChange={e => setSearch(e.target.value)}
                        style={S.input} />
                    {showSuggest && search.trim() && suggestions.length > 0 && (
                        <div style={{ position: 'absolute', left: 0, right: 0, background: C.ink, borderRadius: '0 0 8px 8px', overflow: 'hidden', boxShadow: '0 8px 16px rgba(0,0,0,0.3)', zIndex: 150 }}>
                            {suggestions.map(so => (
                                <div key={so.id} onMouseDown={() => { setSearch(''); setShowSuggest(false); loadSO(so); }}
                                    style={{ padding: '9px 12px', fontSize: '12.5px', color: '#ddd', borderBottom: '1px solid #2a2a2a', cursor: 'pointer' }}>
                                    SO {highlight(so.voucher_no, search)} &nbsp;·&nbsp; PO {so.order_ref_no || '—'} &nbsp;·&nbsp; {highlight(so.buyer, search)}
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            </div>

            <div style={S.body}>
                <div style={{ background: C.red, color: '#fff', borderRadius: '8px', padding: '10px 14px', marginBottom: '14px', fontSize: '12px', fontWeight: 700, textAlign: 'center', letterSpacing: '0.3px' }}>
                    📷 SCAN EAN BARCODE WITH ZEBRA DEVICE
                </div>
                <div style={{ fontSize: '11px', color: C.gray, marginBottom: '10px', fontWeight: 600 }}>{filteredList.length} pending SO{filteredList.length !== 1 ? 's' : ''}</div>
                {loadError && <div style={{ color: C.red, fontSize: '12px', fontWeight: 700, marginBottom: '10px' }}>⚠ {loadError}</div>}

                {listLoading ? (
                    <div style={{ textAlign: 'center', color: C.gray, padding: '60px 0' }}>Loading...</div>
                ) : filteredList.length === 0 ? (
                    <div style={{ textAlign: 'center', padding: '60px 0' }}>
                        <div style={{ fontSize: '44px', marginBottom: '12px', color: C.green }}>✅</div>
                        <div style={{ fontSize: '17px', fontWeight: 700, marginBottom: '8px' }}>All SOs validated</div>
                        <div style={{ fontSize: '13px', color: C.gray }}>No pending Sales Orders</div>
                    </div>
                ) : filteredList.map(so => (
                    <div key={so.id} onClick={() => loadSO(so)} style={{ ...S.card, cursor: 'pointer' }}>
                        <div style={{ background: C.ink, padding: '9px 12px', display: 'flex', justifyContent: 'space-between' }}>
                            <div>
                                <div style={{ fontSize: '15px', fontWeight: 800, fontFamily: 'monospace', color: '#fff' }}>SO {so.voucher_no}</div>
                                <div style={{ fontSize: '10px', color: '#999', fontFamily: 'monospace' }}>PO {so.order_ref_no || '—'}</div>
                                <div style={{ fontSize: '11px', color: '#bbb' }}>{so.buyer}</div>
                            </div>
                            <div style={{ textAlign: 'right' }}>
                                <div style={{ fontSize: '10px', color: '#999' }}>{prettyDate(so.so_date)}</div>
                                {so.kam_name && <div style={{ fontSize: '9.5px', background: C.red, color: '#fff', padding: '2px 7px', borderRadius: '4px', marginTop: '4px', fontWeight: 700, display: 'inline-block' }}>{so.kam_name}</div>}
                                <div style={{ fontSize: '9.5px', padding: '2px 7px', borderRadius: '4px', marginTop: '4px', fontWeight: 700, display: 'inline-block', background: so.kam_validated ? C.greenSoft : '#3a3a3a', color: so.kam_validated ? C.green : '#ccc' }}>
                                    {so.kam_validated ? '✓ KAM Approved' : '🔒 Awaiting KAM'}
                                </div>
                                {so.validation_status === 'half_scanned' && (
                                    <div style={{ fontSize: '9.5px', background: '#FEF3C7', color: '#92400E', padding: '2px 7px', borderRadius: '4px', marginTop: '4px', fontWeight: 700, display: 'inline-block' }}>⏸ Half Scanned</div>
                                )}
                            </div>
                        </div>
                        <div style={{ padding: '10px 12px' }}>
                            <div style={{ display: 'flex', gap: '12px', fontSize: '12px', color: C.gray, marginBottom: so.dispatched_lines > 0 ? '8px' : 0 }}>
                                <span>{so.line_count} SKUs</span>
                                <span>Qty: <strong style={{ color: C.ink }}>{so.total_quantity}</strong></span>
                                {so.consignee && so.consignee !== so.buyer && <span>→ {so.consignee?.slice(0, 20)}</span>}
                            </div>
                            {so.dispatched_lines > 0 && (
                                <div style={{ fontSize: '11px', fontWeight: 700, color: so.dispatched_lines >= so.line_count ? C.green : C.red }}>
                                    {so.dispatched_lines >= so.line_count ? '✓ ' : ''}{so.dispatched_lines}/{so.line_count} SKUs dispatch-scanned
                                </div>
                            )}
                        </div>
                    </div>
                ))}
            </div>
        </div>
    );

    // ── LOCKED — not yet KAM-approved / already validated ────────────────────
    if (lockedGate) return (
        <div style={S.page}>
            <div style={{ ...S.overlay, position: 'fixed', alignItems: 'center' }}>
                <div style={{ ...S.modal, borderRadius: '16px', textAlign: 'center', padding: '36px 24px' }}>
                    <div style={{ fontSize: '44px', marginBottom: '14px' }}>🔒</div>
                    <div style={{ fontSize: '17px', fontWeight: 800, marginBottom: '10px' }}>
                        {lockedGate.code === 'already_validated' ? 'Already Validated' : 'Not Yet Approved'}
                    </div>
                    <div style={{ fontSize: '13px', color: C.gray, marginBottom: '24px', lineHeight: 1.5 }}>{lockedGate.message}</div>
                    <button onClick={() => { setLockedGate(null); resetToLoadScreen(); }} style={S.priBtn}>OK</button>
                </div>
            </div>
        </div>
    );

    // ── SUCCESS ──────────────────────────────────────────────────────────
    if (successPopup) return (
        <div style={S.page}>
            <div style={{ ...S.overlay, position: 'fixed', alignItems: 'center' }}>
                <div style={{ ...S.modal, borderRadius: '16px', border: `2px solid ${C.green}`, textAlign: 'center', padding: '40px 24px' }}>
                    <div style={{ fontSize: '56px', marginBottom: '16px', color: C.green }}>✅</div>
                    <div style={{ fontSize: '22px', fontWeight: 800, marginBottom: '8px' }}>Validated!</div>
                    <div style={{ fontSize: '14px', color: C.gray, marginBottom: '28px' }}>SO {header?.voucher_no} (PO {header?.order_ref_no || '—'}) has been saved</div>
                    <button onClick={resetToLoadScreen} style={{ ...S.priBtn, background: C.green }}>→ Next SO</button>
                </div>
            </div>
        </div>
    );

    // ── BOXES/WEIGHT POPUP (shown before Final Check) ────────────────────
    if (boxCountPopup) return (
        <div style={S.page}>
            <div style={S.overlay}>
                <div style={S.modal}>
                    <div style={{ fontSize: '16px', fontWeight: 700, marginBottom: '4px' }}>{vendorType === 'plain' ? 'How many boxes packed?' : 'Total weight'}</div>
                    <div style={{ fontSize: '12px', color: C.gray, marginBottom: '16px' }}>SO {header?.voucher_no} · PO {header?.order_ref_no || '—'}</div>
                    {vendorType === 'plain' && (
                        <>
                            <input type="number" inputMode="numeric" value={boxCount} min={0} autoFocus
                                onChange={e => { setBoxCount(e.target.value); setBoxCountError(''); }}
                                onKeyDown={e => e.key === 'Enter' && confirmBoxCount()}
                                style={{ width: '100%', padding: '12px', border: `2px solid ${boxCountError ? C.red : C.ink}`, borderRadius: '8px', fontSize: '26px', fontWeight: 800, textAlign: 'center', marginBottom: '6px', outline: 'none' }}
                                placeholder="0" />
                            {boxCountError && <div style={{ textAlign: 'center', fontSize: '12px', fontWeight: 700, color: C.red, marginBottom: '10px' }}>⚠ {boxCountError}</div>}
                        </>
                    )}
                    {vendorType !== 'plain' && (
                        <div style={{ fontSize: '12px', color: C.gray, marginBottom: '10px' }}>{sealedCount} box{sealedCount === 1 ? '' : 'es'} sealed — count is derived automatically.</div>
                    )}
                    <div style={{ fontSize: '13px', fontWeight: 700, marginBottom: '4px', marginTop: '12px' }}>Total weight of boxes (kg)?</div>
                    <input type="number" inputMode="decimal" step="0.01" value={totalWeight} min={0} autoFocus={vendorType !== 'plain'}
                        onChange={e => { setTotalWeight(e.target.value); setTotalWeightError(''); }}
                        onKeyDown={e => e.key === 'Enter' && confirmBoxCount()}
                        style={{ width: '100%', padding: '12px', border: `2px solid ${totalWeightError ? C.red : C.ink}`, borderRadius: '8px', fontSize: '26px', fontWeight: 800, textAlign: 'center', marginBottom: '6px', outline: 'none' }}
                        placeholder="0.00" />
                    {totalWeightError && <div style={{ textAlign: 'center', fontSize: '12px', fontWeight: 700, color: C.red, marginBottom: '10px' }}>⚠ {totalWeightError}</div>}
                    <div style={{ display: 'flex', gap: '10px', marginTop: '8px' }}>
                        <button onClick={() => setBoxCountPopup(false)} style={{ ...S.secBtn, flex: 1 }}>Cancel</button>
                        <button onClick={confirmBoxCount} style={{ flex: 2, padding: '14px', background: C.ink, color: '#fff', border: 'none', borderRadius: '8px', fontSize: '15px', fontWeight: 700, cursor: 'pointer' }}>Continue</button>
                    </div>
                </div>
            </div>
        </div>
    );

    // ── FINAL CHECK — review screen ───────────────────────────────────────
    if (finalCheckOpen) return (
        <div style={S.page}>
            <div style={S.overlay}>
                <div style={{ ...S.modal, borderRadius: '16px', border: `2px solid ${C.ink}`, padding: '0', maxHeight: '88vh', display: 'flex', flexDirection: 'column' }}>
                    <div style={{ background: C.ink, padding: '16px 20px', borderRadius: '14px 14px 0 0' }}>
                        <div style={{ fontSize: '10px', letterSpacing: '2px', color: C.red, fontWeight: 800, textTransform: 'uppercase' }}>Final Check</div>
                        <div style={{ fontSize: '17px', fontWeight: 800, color: '#fff' }}>SO {header?.voucher_no} · {header?.buyer}</div>
                        <div style={{ fontSize: '11px', color: '#999', fontFamily: 'monospace', marginTop: '2px' }}>PO {header?.order_ref_no || '—'}</div>
                        <div style={{ fontSize: '12px', color: '#bbb', marginTop: '4px' }}>📦 {vendorType === 'plain' ? (boxCount || 0) : sealedCount} box{(vendorType === 'plain' ? Number(boxCount) : sealedCount) !== 1 ? 'es' : ''} · {totalWeight || 0} kg</div>
                    </div>

                    <div style={{ overflowY: 'auto', flex: 1, padding: '6px 20px' }}>
                        <div style={{ display: 'flex', fontSize: '10px', color: C.gray, textTransform: 'uppercase', fontWeight: 700, padding: '10px 0 6px', borderBottom: `2px solid ${C.ink}` }}>
                            <div style={{ flex: 1 }}>Item</div>
                            <div style={{ width: '56px', textAlign: 'right' }}>To Pack</div>
                            <div style={{ width: '56px', textAlign: 'right' }}>{vendorType === 'plain' ? 'Disp' : 'Packed'}</div>
                        </div>
                        {lines.map(l => {
                            const dispatchQty = dispatchQtyFor(l) ?? 0;
                            const ref = refQty(l);
                            const mismatch = dispatchQty !== ref;
                            const boxSummary = vendorType !== 'plain' ? boxOnlySummaryOrRangeFor(l, boxes) : null;
                            return (
                                <div key={l.id} style={{ padding: '10px 0', borderBottom: `1px solid ${C.line}` }}>
                                    <div style={{ display: 'flex', alignItems: 'flex-start' }}>
                                        <div style={{ flex: 1, minWidth: 0, paddingRight: '8px' }}>
                                            <div style={{ fontSize: '12.5px', fontWeight: 700, lineHeight: '1.3' }}>{l.sku_name}</div>
                                            <div style={{ fontSize: '10px', color: C.gray, fontFamily: 'monospace' }}>{l.ean}</div>
                                        </div>
                                        <div style={{ width: '56px', textAlign: 'right', fontSize: '13px', fontWeight: 700, flexShrink: 0 }}>{ref}</div>
                                        <div style={{ width: '56px', textAlign: 'right', fontSize: '13px', fontWeight: 800, color: mismatch ? C.red : C.green, flexShrink: 0 }}>{dispatchQty}</div>
                                    </div>
                                    {boxSummary && <div style={{ fontSize: '10.5px', color: C.gray, marginTop: '3px' }}>📦 {boxSummary}</div>}
                                    {vendorType === 'box_batch' && l.batch_no && (
                                        <div style={{ fontSize: '10.5px', color: C.gray, marginTop: '2px' }}>Batch {l.batch_no} · Mfg {formatDate(l.mfg_date)} · Exp {formatDate(l.exp_date)}</div>
                                    )}
                                </div>
                            );
                        })}

                        <div style={{ padding: '12px 0', borderTop: `1px solid ${C.line}`, marginTop: '4px' }}>
                            <div style={{ fontSize: '11px', color: C.gray, fontWeight: 600, marginBottom: '8px' }}>📷 {soImages.length} box image{soImages.length !== 1 ? 's' : ''} uploaded</div>
                            {soImages.length > 0 && (
                                <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
                                    {soImages.slice(0, 8).map((img, i) => (
                                        <img key={img.id ?? img.tempId ?? i} src={img.dataUrl} alt={`box-${i}`} style={{ width: '44px', height: '44px', objectFit: 'cover', borderRadius: '6px', border: `1px solid ${C.line}` }} />
                                    ))}
                                </div>
                            )}
                        </div>
                    </div>

                    <div style={{ padding: '14px 20px 20px' }}>
                        <div style={{ fontSize: '10.5px', color: C.gray, textAlign: 'center', marginBottom: '8px' }}>
                            {vendorType === 'plain' ? 'To correct a quantity go back and rescan that EAN.' : 'To correct a box, go back and edit/delete it.'}
                        </div>
                        <div style={{ display: 'flex', gap: '10px' }}>
                            <button onClick={() => setFinalCheckOpen(false)} style={{ ...S.secBtn, flex: 1 }}>Back</button>
                            <button onClick={() => { if (!hasEditAccess(user, 'warehouse', 'warehouse_access')) return notifyEditBlocked(); finalSave(); }} disabled={saving}
                                style={{ flex: 2, padding: '14px', background: C.red, color: '#fff', border: 'none', borderRadius: '8px', fontSize: '15px', fontWeight: 700, cursor: 'pointer' }}>
                                {saving ? 'Saving...' : '✓ Confirm & Save'}
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );

    // ── MAIN SCAN VIEW ─────────────────────────────────────────────────────
    // Gallery + SO summary + scan banner — shown above the box panel (and
    // pinned there, not scrolling with it) for box_batch, or as the top of
    // the single scrolling list for plain/box_only.
    const topContent = (
        <>
            <div style={{ marginBottom: '14px', display: 'flex', gap: '6px', flexWrap: 'wrap', justifyContent: 'flex-start', alignItems: 'center' }}>
                <button onClick={() => openCamera('gallery')} title="Box photos" style={{ background: 'none', border: 'none', fontSize: '27.5px', cursor: 'pointer', padding: 0, lineHeight: 1, flexShrink: 0 }}>📷</button>
                {soImages.slice(0, 4).map((img, idx) => (
                    <div key={img.id ?? img.tempId ?? idx} style={{ position: 'relative', width: '40px', height: '40px', flexShrink: 0 }}>
                        <img src={img.dataUrl} alt={`box-${idx}`} onClick={() => img.failed ? retrySoImage(img) : setExpandedImage(img)} style={{ width: '40px', height: '40px', objectFit: 'cover', borderRadius: '6px', border: `1.5px solid ${img.failed ? C.red : C.ink}`, opacity: img.uploading ? 0.5 : (img.failed ? 0.6 : 1), cursor: 'pointer' }} />
                        {img.uploading && <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '9px', color: '#fff', fontWeight: 700, background: 'rgba(0,0,0,0.35)', borderRadius: '6px' }}>...</div>}
                        {img.failed && <div onClick={() => retrySoImage(img)} title="Upload failed — tap to retry" style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '9px', color: '#fff', fontWeight: 700, background: 'rgba(200,0,0,0.55)', borderRadius: '6px', cursor: 'pointer' }}>⚠ retry</div>}
                        <button onClick={() => removeSoImage(idx)} style={{ position: 'absolute', top: '-5px', right: '-5px', background: C.red, color: '#fff', border: 'none', borderRadius: '50%', width: '16px', height: '16px', fontSize: '10px', cursor: 'pointer', lineHeight: '16px', textAlign: 'center', padding: 0 }}>✕</button>
                    </div>
                ))}
                {soImages.length > 4 && (
                    <button onClick={() => setGalleryModalOpen(true)} style={{ width: '40px', height: '40px', borderRadius: '6px', border: `1.5px solid ${C.ink}`, background: C.panel, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '11px', fontWeight: 800, color: C.ink, cursor: 'pointer', flexShrink: 0 }}>+{soImages.length - 4}</button>
                )}
                {soImages.length > 0 && (
                    <span style={{ marginLeft: 'auto', fontSize: '13px', color: C.ink, flexShrink: 0 }}>Image Count: <b style={{ fontWeight: 800 }}>{soImages.length}</b></span>
                )}
            </div>
        </>
    );

    const footerButtons = (
        <div style={{ display: 'flex', gap: '10px' }}>
            {vendorType === 'plain' && (
                <button onClick={saveProgressAndExit} disabled={scannedCount === 0 || saving}
                    style={{ ...S.secBtn, flex: 1, padding: '10px 16px', fontSize: '14px', opacity: scannedCount === 0 ? 0.6 : 1 }}>
                    {saving ? 'Saving...' : '⏸ Save Progress & Exit'}
                </button>
            )}
            {vendorType !== 'plain' && (
                <button onClick={resetToLoadScreen} style={{ ...S.secBtn, flex: 1, padding: '10px 16px', fontSize: '14px' }}>← Exit (auto-saved)</button>
            )}
            <button onClick={() => scannedCount > 0 && setBoxCountPopup(true)} disabled={scannedCount === 0}
                style={{ ...S.priBtn, flex: 2, marginTop: 0, padding: '10px 16px', background: scannedCount > 0 ? C.ink : '#ccc', opacity: scannedCount > 0 ? 1 : 0.6 }}>
                {scannedCount === 0 ? 'Scan SKUs to continue' : `Final Check (${scannedCount}/${lines.length})`}
            </button>
        </div>
    );

    return (
        <div style={{ ...S.page, height: '100vh', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
            <input ref={scanInputRef} onKeyDown={e => { if (e.key === 'Enter' || e.key === 'Tab') { e.preventDefault(); finishScan(e.currentTarget.value); } }}
                onChange={e => { const v = e.target.value; clearTimeout(scanDebounce.current); scanDebounce.current = setTimeout(() => { if (v.trim().length > 3) finishScan(v); }, 150); }}
                onBlur={() => setTimeout(focusScan, 50)}
                inputMode="none" autoComplete="off" autoCorrect="off" autoCapitalize="off" spellCheck={false} aria-hidden="true"
                style={{ position: 'absolute', opacity: 0, height: 0, width: 0, border: 'none', padding: 0, margin: 0, pointerEvents: 'none' }} />

            <div style={{ ...S.header, flexShrink: 0 }}>
                <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: '12px', marginBottom: '10px' }}>
                    <div style={{ display: 'flex', alignItems: 'flex-start', gap: '12px' }}>
                        {onExit && <button onClick={resetToLoadScreen} style={{ background: 'none', border: 'none', color: '#fff', fontSize: '20px', cursor: 'pointer', padding: 0, marginTop: '-2px' }}>←</button>}
                        <div>
                            <div style={{ display: 'flex', alignItems: 'baseline', gap: '8px' }}>
                                <span style={{ fontSize: '18px', fontWeight: 800, color: '#fff' }}>SO {header?.voucher_no || '...'}</span>
                                <span style={{ fontSize: '12.1px', color: '#999', fontFamily: 'monospace' }}>PO {header?.order_ref_no || '—'}</span>
                            </div>
                            <div style={{ fontSize: '12px', color: '#bbb' }}>{header?.buyer}</div>
                        </div>
                    </div>
                    {header && (
                        <div style={{ textAlign: 'right', flexShrink: 0 }}>
                            <div style={{ fontSize: '11px', color: '#999' }}>{formatDate(header.so_date)}</div>
                            <div style={{ fontSize: '12px', color: '#fff', fontWeight: 700 }}>Total: {header.total_quantity}</div>
                        </div>
                    )}
                </div>
                {lines.length > 0 && (
                    <div>
                        <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '11px', color: '#ccc', marginBottom: '4px' }}>
                            <span>{scannedCount} of {lines.length} {vendorType === 'plain' ? 'scanned' : 'packed'}</span>
                            <span>{Math.round((scannedCount / lines.length) * 100)}%</span>
                        </div>
                        <div style={{ background: '#333', borderRadius: '4px', height: '5px' }}>
                            <div style={{ width: `${(scannedCount / lines.length) * 100}%`, height: '100%', background: C.red, borderRadius: '4px', transition: 'width 0.3s' }} />
                        </div>
                    </div>
                )}
            </div>

            {vendorType === 'box_batch' ? (
            <div style={{ flex: 1, overflowY: 'auto', padding: '14px' }}>
                {topContent}

                {/* ── Box strip — one icon-card per box, scrollable left/right.
                     Tap any box (sealed or open) to view its full details
                     below; the currently-open/scanning box always gets a
                     highlighted (red) border regardless of what's selected. ── */}
                <div style={{ display: 'flex', gap: '8px', overflowX: 'auto', paddingBottom: '8px', marginBottom: '4px' }}>
                    {boxes.slice().sort((a, b) => a.number - b.number).map(b => {
                        const units = b.items.reduce((s, i) => s + Number(i.qty), 0);
                        const isOpen = !b.sealed;
                        const isViewed = (viewedBoxNumber ?? activeBox?.number) === b.number;
                        return (
                            <button key={b.number} onClick={() => setViewedBoxNumber(b.number)}
                                style={{
                                    flexShrink: 0, minWidth: '68px', textAlign: 'center', padding: '8px 10px', borderRadius: '10px', cursor: 'pointer',
                                    border: isOpen ? `2px solid ${C.red}` : (isViewed ? `2px solid ${C.ink}` : `1.5px solid ${C.line}`),
                                    background: isOpen ? C.redSoft : (b.sealed ? C.greenSoft : C.white),
                                }}>
                                <div style={{ fontSize: '20px', lineHeight: 1 }}>📦</div>
                                <div style={{ fontSize: '11px', fontWeight: 800, marginTop: '2px' }}>Box {b.number}</div>
                                <div style={{ fontSize: '9.5px', color: C.gray }}>{b.items.length} SKU</div>
                                <div style={{ fontSize: '9.5px', color: C.gray }}>{units} qty</div>
                            </button>
                        );
                    })}
                </div>

                {/* ── Large detail panel for whichever box is selected above ── */}
                {(() => {
                    const viewed = boxes.find(b => b.number === (viewedBoxNumber ?? activeBox?.number));
                    if (!viewed) return null;
                    const b = viewed;
                    const units = b.items.reduce((s, i) => s + Number(i.qty), 0);
                    const isOpen = !b.sealed;
                    const editing = editingBox === b.number && b.items.length === 1;
                    return (
                        <div style={{ ...S.card, border: `2px solid ${isOpen ? C.red : C.green}`, background: isOpen ? C.redSoft : C.greenSoft, padding: '12px 14px' }}>
                            <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: '8px' }}>
                                <div>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                                        <span style={{ fontWeight: 800, fontSize: '13px' }}>Box {b.number}</span>
                                        {isOpen
                                            ? <span style={{ fontSize: '9px', fontWeight: 800, color: C.redDeep, background: '#fff', padding: '2px 6px', borderRadius: '4px', border: `1px solid ${C.red}` }}>SCANNING HERE</span>
                                            : <span style={{ color: C.green }}>✅</span>}
                                    </div>
                                    <div style={{ fontSize: '11px', color: C.gray, marginTop: '2px' }}>{b.items.length} SKU(s) · {units} pcs</div>
                                </div>
                                {isOpen ? (
                                    <button onClick={boxFullTapped} disabled={b.items.length === 0 || savingBox}
                                        style={{ flexShrink: 0, padding: '6px 10px', background: b.items.length ? C.ink : '#ccc', color: '#fff', border: 'none', borderRadius: '7px', fontSize: '11px', fontWeight: 700, cursor: b.items.length ? 'pointer' : 'default' }}>
                                        {savingBox ? '…' : '📦 Full'}
                                    </button>
                                ) : (
                                    <span style={{ display: 'flex', gap: '2px', flexShrink: 0 }}>
                                        {b.items.length === 1 && <button onClick={() => startEditBox(b)} title="Edit qty" style={{ background: 'none', border: 'none', fontSize: '16px', cursor: 'pointer', padding: '4px' }}>✏️</button>}
                                        <button onClick={() => deleteBox(b.number)} title="Delete box" style={{ background: 'none', border: 'none', color: C.red, fontSize: '16px', cursor: 'pointer', padding: '4px' }}>🗑️</button>
                                    </span>
                                )}
                            </div>
                            {editing ? (
                                <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginTop: '8px' }}>
                                    <input autoFocus type="number" value={editQtyInput} onChange={e => setEditQtyInput(e.target.value)}
                                        onKeyDown={e => e.key === 'Enter' && editBoxQty(b)}
                                        style={{ width: '70px', padding: '5px 7px', border: `1.5px solid ${editError ? C.red : C.ink}`, borderRadius: '6px', fontSize: '13px', fontWeight: 700 }} />
                                    <button onClick={() => editBoxQty(b)} disabled={savingBox} style={{ background: C.ink, color: '#fff', border: 'none', borderRadius: '5px', padding: '5px 10px', fontSize: '11.5px', fontWeight: 700, cursor: 'pointer' }}>Save</button>
                                    <button onClick={cancelEditBox} style={{ background: '#eee', border: 'none', borderRadius: '5px', padding: '5px 10px', fontSize: '11.5px', cursor: 'pointer' }}>✕</button>
                                    {editError && <span style={{ fontSize: '10.5px', color: C.red, fontWeight: 700 }}>⚠ {editError}</span>}
                                </div>
                            ) : b.items.length > 0 && (
                                <div style={{ marginTop: '8px', borderTop: `1px solid ${C.line}`, paddingTop: '6px' }}>
                                    {b.items.map(it => (
                                        <div key={it.line_id} style={{ display: 'flex', justifyContent: 'space-between', fontSize: '11.5px', padding: '2px 0' }}>
                                            <span style={{ color: C.inkSoft }}>{it.sku_name}</span><b>×{it.qty}</b>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                    );
                })()}

                {lines.length === 0 ? (
                    <div style={{ textAlign: 'center', color: C.gray, padding: '40px' }}>Loading...</div>
                ) : lines.map(l => {
                    const done = isLineDone(l);
                    const dispatchQty = dispatchQtyFor(l);
                    const ref = refQty(l);
                    const mismatch = done && dispatchQty !== ref;
                    return (
                        <div key={l.id} onClick={() => { if (done && !boxWriteInFlightRef.current) scanIntoBox(l); }}
                            style={{ ...S.card, height: '120px', border: done ? `2px solid ${mismatch ? C.red : C.green}` : `1.5px solid ${C.line}`, cursor: done ? 'pointer' : 'default' }}>
                            <div style={{ display: 'flex', alignItems: 'stretch', height: '100%' }}>
                                <div style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column' }}>
                                    {/* ── Section: SKU name + EAN — fixed height regardless of the hint line ── */}
                                    <div style={{ height: '76px', padding: '10px 12px', borderBottom: `1px solid ${C.line}` }}>
                                        <div style={{ display: 'flex', alignItems: 'flex-start', gap: '6px' }}>
                                            <div style={{ fontSize: '13px', fontWeight: 600, lineHeight: '1.3' }}>{l.sku_name}</div>
                                            {done && <div style={{ fontSize: '16px', flexShrink: 0 }}>{mismatch ? '⚠️' : '✅'}</div>}
                                        </div>
                                        <div style={{ fontSize: '11px', color: C.gray, fontFamily: 'monospace', marginTop: '3px' }}>EAN {l.ean}</div>
                                    </div>
                                    {/* ── Section: batch/mfg/exp, with headers, laid out horizontally —
                                         always rendered (placeholders until saved) so every card is the
                                         same fixed height regardless of whether batch info exists yet. ── */}
                                    <div style={{ flex: 1, padding: '8px 12px', display: 'flex', alignItems: 'flex-start', gap: '6px' }}>
                                        <div style={{ flex: 1, display: 'flex', gap: '10px' }}>
                                            <div style={{ fontSize: '10.5px', color: C.inkSoft }}>
                                                <div style={{ fontSize: '8px', color: C.gray, textTransform: 'uppercase' }}>Batch No.</div>
                                                <b>{l.batch_no || '—'}</b>
                                            </div>
                                            <div style={{ fontSize: '10.5px', color: C.inkSoft }}>
                                                <div style={{ fontSize: '8px', color: C.gray, textTransform: 'uppercase' }}>Mfg Date</div>
                                                <b>{formatDate(l.mfg_date)}</b>
                                            </div>
                                            <div style={{ fontSize: '10.5px', color: C.inkSoft }}>
                                                <div style={{ fontSize: '8px', color: C.gray, textTransform: 'uppercase' }}>Exp Date</div>
                                                <b>{formatDate(l.exp_date)}</b>
                                            </div>
                                        </div>
                                        <button onClick={e => { e.stopPropagation(); editBatchInfo(l); }} title="Edit batch details"
                                            style={{ background: 'none', border: 'none', fontSize: '13px', cursor: 'pointer', padding: '2px', lineHeight: 1, flexShrink: 0, visibility: l.batch_no ? 'visible' : 'hidden' }}>✏️</button>
                                    </div>
                                </div>
                                {/* ── Section: Packed / To Pack ── */}
                                <div style={{ width: '20%', flexShrink: 0, borderLeft: `1px solid ${C.line}`, display: 'flex', flexDirection: 'column' }}>
                                    <div style={{ flex: 1, textAlign: 'center', padding: '8px 2px', borderBottom: `1px solid ${C.line}`, background: done ? (mismatch ? C.redSoft : C.greenSoft) : C.panel, display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
                                        <div style={{ fontSize: '7.5px', color: C.gray, textTransform: 'uppercase' }}>Packed</div>
                                        <div style={{ fontSize: '13px', fontWeight: 800, color: done ? (mismatch ? C.red : C.green) : C.gray }}>{done ? dispatchQty : '—'}</div>
                                    </div>
                                    <div style={{ flex: 1, textAlign: 'center', padding: '8px 2px', display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
                                        <div style={{ fontSize: '7.5px', color: C.gray, textTransform: 'uppercase' }}>To Pack</div>
                                        <div style={{ fontSize: '13px', fontWeight: 800 }}>{ref}</div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    );
                })}

                <div style={{ marginTop: '8px' }}>{footerButtons}</div>
            </div>
            ) : (
            <div style={{ flex: 1, overflowY: 'auto', padding: '14px' }}>
                {topContent}

                {/* ── SKU list — same 3-column card as production, for plain & box_only ── */}
                {lines.length === 0 ? (
                    <div style={{ textAlign: 'center', color: C.gray, padding: '40px' }}>Loading...</div>
                ) : lines.map(l => {
                    const done = isLineDone(l);
                    const dispatchQty = dispatchQtyFor(l);
                    const ref = refQty(l);
                    const mismatch = done && dispatchQty !== ref;
                    const boxSummary = vendorType === 'box_only' ? boxOnlySummaryFor(l) : null;
                    return (
                        <div key={l.id}
                            onClick={() => { if (!done || boxWriteInFlightRef.current) return; if (vendorType === 'plain') openLinePopup(l); if (vendorType === 'box_only') openBoxOnlyEntry(l); }}
                            style={{ ...S.card, height: '104px', border: done ? `2px solid ${mismatch ? C.red : C.green}` : `1.5px solid ${C.line}`, cursor: done ? 'pointer' : 'default' }}>
                            <div style={{ padding: '12px 14px', height: '100%', display: 'flex', flexDirection: 'column' }}>
                                <div style={{ display: 'flex', alignItems: 'flex-start' }}>
                                    <div style={{ flex: 1, minWidth: 0, marginRight: '8px' }}>
                                        <div style={{ display: 'flex', alignItems: 'flex-start', gap: '6px' }}>
                                            <div style={{ fontSize: '13px', fontWeight: 600, lineHeight: '1.3' }}>{l.sku_name}</div>
                                            {done && <div style={{ fontSize: '16px', flexShrink: 0 }}>{mismatch ? '⚠️' : '✅'}</div>}
                                        </div>
                                        <div style={{ fontSize: '11px', color: C.gray, fontFamily: 'monospace', marginTop: '3px' }}>{l.ean}</div>
                                        {vendorType === 'plain' && <div style={{ fontSize: '10px', color: C.gray, marginTop: '6px', visibility: done ? 'hidden' : 'visible' }}>Scan barcode to enter quantity</div>}
                                        {vendorType === 'box_only' && (
                                            <div style={{ fontSize: '10.5px', color: boxSummary?.mismatch ? C.red : C.gray, fontWeight: 700, marginTop: '6px', visibility: boxSummary ? 'visible' : 'hidden' }}>
                                                {boxSummary && (<>{boxSummary.mismatch && '⚠ '}{boxSummary.count} box{boxSummary.count === 1 ? '' : 'es'} · Box {boxSummary.first}{boxSummary.count > 1 ? ` to ${boxSummary.last}` : ''} · {boxSummary.perBox != null ? `${boxSummary.count} × ${boxSummary.perBox}` : `${boxSummary.packedQty} (varied)`}</>)}
                                            </div>
                                        )}
                                    </div>
                                    <div style={{ width: '20%', flexShrink: 0, border: `1px solid ${C.line}`, borderRadius: '8px', overflow: 'hidden' }}>
                                        <div style={{ textAlign: 'center', padding: '5px 2px', borderBottom: `1px solid ${C.line}`, background: done ? (mismatch ? C.redSoft : C.greenSoft) : C.panel }}>
                                            <div style={{ fontSize: '7.5px', color: C.gray, textTransform: 'uppercase' }}>{vendorType === 'plain' ? 'Dispatch' : 'Packed'}</div>
                                            <div style={{ fontSize: '13px', fontWeight: 800, color: done ? (mismatch ? C.red : C.green) : C.gray }}>{done ? dispatchQty : '—'}</div>
                                        </div>
                                        <div style={{ textAlign: 'center', padding: '5px 2px' }}>
                                            <div style={{ fontSize: '7.5px', color: C.gray, textTransform: 'uppercase' }}>To Pack</div>
                                            <div style={{ fontSize: '13px', fontWeight: 800 }}>{ref}</div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    );
                })}

                <div style={{ marginTop: '8px' }}>{footerButtons}</div>
            </div>
            )}

            {/* ── Unknown EAN / pick-a-line / scan qty popup (plain & box_batch) ── */}
            {popup && (
                <div style={S.overlay}>
                    <div style={S.modal}>
                        {popup.type === 'unknown' ? (
                            <>
                                <div style={{ fontSize: '32px', textAlign: 'center', marginBottom: '12px' }}>⚠️</div>
                                <div style={{ fontSize: '16px', fontWeight: 700, textAlign: 'center', marginBottom: '8px' }}>Unknown EAN</div>
                                <div style={{ fontSize: '13px', color: C.gray, textAlign: 'center', marginBottom: '20px' }}>
                                    EAN <span style={{ fontFamily: 'monospace', color: C.red, fontWeight: 700 }}>{popup.ean}</span> is not in SO <b>{popup.voucherNo}</b> (PO {header?.order_ref_no || '—'})
                                </div>
                                <button onClick={() => { setPopup(null); setTimeout(focusScan, 50); }} style={S.priBtn}>OK</button>
                            </>
                        ) : popup.type === 'pick' ? (
                            <>
                                <div style={{ fontSize: '12px', color: C.gray, marginBottom: '4px', fontFamily: 'monospace' }}>{popup.ean}</div>
                                <div style={{ fontSize: '16px', fontWeight: 700, marginBottom: '6px' }}>{popup.skuName}</div>
                                <div style={{ fontSize: '13px', color: C.gray, marginBottom: '14px' }}>On <b>{popup.matches.length} lines</b> of this SO — pick which one you're counting:</div>
                                {popup.matches.map((l, i) => {
                                    const done = isLineDone(l);
                                    return (
                                        <button key={l.id} onClick={() => { setPopup(null); routeScan(l); }}
                                            style={{ width: '100%', textAlign: 'left', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '10px', padding: '13px 14px', marginBottom: '9px', background: done ? C.greenSoft : C.white, border: `1.5px solid ${done ? C.green : C.ink}`, borderRadius: '10px', font: 'inherit' }}>
                                            <div>
                                                <div style={{ fontSize: '14px', fontWeight: 800 }}>Line {i + 1} of {popup.matches.length}</div>
                                                <div style={{ fontSize: '12px', color: C.gray, marginTop: '2px' }}>Ordered {refQty(l)}</div>
                                            </div>
                                            <div style={{ fontSize: '12px', fontWeight: 700, color: done ? C.green : C.gray }}>{done ? `✓ ${dispatchQtyFor(l)}` : 'not done'}</div>
                                        </button>
                                    );
                                })}
                                <button onClick={() => { setPopup(null); setTimeout(focusScan, 50); }} style={{ ...S.secBtn, width: '100%', marginTop: '6px', padding: '13px' }}>Cancel</button>
                            </>
                        ) : (
                            <>
                                {popup.type === 'rescan' && (
                                    <div style={{ background: C.redSoft, borderRadius: '8px', padding: '10px 14px', marginBottom: '14px', display: 'flex', alignItems: 'center', gap: '8px', border: `1px solid ${C.red}` }}>
                                        <span style={{ fontSize: '18px' }}>⚠️</span><span style={{ fontSize: '13px', color: C.redDeep, fontWeight: 600 }}>Already scanned — previous qty: {popup.existing}</span>
                                    </div>
                                )}
                                <div style={{ fontSize: '12px', color: C.gray, marginBottom: '4px', fontFamily: 'monospace' }}>{popup.ean}</div>
                                <div style={{ fontSize: '16px', fontWeight: 700, marginBottom: '12px' }}>{popup.skuName}</div>
                                <div style={{ textAlign: 'center', padding: '10px 6px', border: `1px solid ${C.line}`, borderRadius: '8px', marginBottom: '16px' }}>
                                    <div style={{ fontSize: '9.5px', color: C.gray, textTransform: 'uppercase' }}>To Pack</div>
                                    <div style={{ fontSize: '17px', fontWeight: 800 }}>{popup.kamQty ?? popup.poQty}</div>
                                </div>
                                <div style={{ fontSize: '13px', color: C.gray, marginBottom: '8px' }}>Enter dispatch quantity:</div>
                                <input ref={qtyInputRef} type="number" value={qtyInput} max={popup.poQty} min={0}
                                    onChange={e => { setQtyInput(e.target.value); setQtyError(''); }}
                                    onKeyDown={e => e.key === 'Enter' && savePopupQty()}
                                    style={{ width: '100%', padding: '12px', border: `2px solid ${qtyError ? C.red : C.ink}`, borderRadius: '8px', fontSize: '26px', fontWeight: 800, textAlign: 'center', marginBottom: '6px', outline: 'none' }}
                                    placeholder="0" />
                                {qtyError ? (
                                    <div style={{ textAlign: 'center', fontSize: '12px', fontWeight: 700, color: C.red, marginBottom: '10px' }}>⚠ {qtyError}</div>
                                ) : qtyInput !== '' && !isNaN(parseFloat(qtyInput)) && (() => {
                                    const ref = popup.kamQty ?? popup.poQty;
                                    const diff = parseFloat(qtyInput) - ref;
                                    return diff !== 0 ? (
                                        <div style={{ textAlign: 'center', fontSize: '12px', fontWeight: 700, color: C.red, marginBottom: '10px' }}>{diff > 0 ? '+' : ''}{diff} vs {popup.kamQty != null ? 'KAM' : 'PO'} qty</div>
                                    ) : (
                                        <div style={{ textAlign: 'center', fontSize: '12px', fontWeight: 700, color: C.green, marginBottom: '10px' }}>✓ Matches</div>
                                    );
                                })()}
                                <div style={{ display: 'flex', gap: '10px', marginTop: '8px' }}>
                                    <button onClick={() => { setPopup(null); setQtyInput(''); setQtyError(''); setTimeout(focusScan, 50); }} style={{ ...S.secBtn, flex: 1 }}>Cancel</button>
                                    <button onClick={savePopupQty} style={{ flex: 2, padding: '14px', background: C.ink, color: '#fff', border: 'none', borderRadius: '8px', fontSize: '15px', fontWeight: 700, cursor: 'pointer' }}>Save</button>
                                </div>
                            </>
                        )}
                    </div>
                </div>
            )}

            {/* ── Type-3 box qty popup ── */}
            {boxQtyPopup && (
                <div style={S.overlay}>
                    <div style={S.modal}>
                        <div style={{ fontSize: '12px', color: C.gray, fontFamily: 'monospace', marginBottom: '4px' }}>{boxQtyPopup.ean}</div>
                        <div style={{ fontSize: '16px', fontWeight: 700, marginBottom: '4px' }}>{boxQtyPopup.sku_name}</div>
                        <div style={{ fontSize: '12px', color: C.gray, marginBottom: '14px' }}>{remainingBox(boxQtyPopup)} remaining (PO qty) · going into Box {activeBox?.number}</div>
                        <input autoFocus type="number" value={boxQtyInput} onChange={e => { setBoxQtyInput(e.target.value); setBoxQtyError(''); }}
                            onKeyDown={e => e.key === 'Enter' && saveBoxQty()}
                            style={{ width: '100%', padding: '12px', border: `2px solid ${boxQtyError ? C.red : C.ink}`, borderRadius: '8px', fontSize: '26px', fontWeight: 800, textAlign: 'center', marginBottom: '6px', outline: 'none' }}
                            placeholder="0" />
                        {boxQtyError && <div style={{ textAlign: 'center', fontSize: '12px', fontWeight: 700, color: C.red, marginBottom: '10px' }}>⚠ {boxQtyError}</div>}
                        <div style={{ display: 'flex', gap: '10px', marginTop: '8px' }}>
                            <button onClick={() => setBoxQtyPopup(null)} style={{ ...S.secBtn, flex: 1 }}>Cancel</button>
                            <button onClick={saveBoxQty} style={{ flex: 2, padding: '14px', background: C.ink, color: '#fff', border: 'none', borderRadius: '8px', fontWeight: 700, cursor: 'pointer' }}>Add to Box {activeBox?.number}</button>
                        </div>
                    </div>
                </div>
            )}

            {/* ── Type-2 box-only entry ── */}
            {boxOnlyEntry && !boxOnlyConfirm && (
                <div style={S.overlay}>
                    <div style={S.modal}>
                        <div style={{ fontSize: '12px', color: C.gray, fontFamily: 'monospace', marginBottom: '2px' }}>{boxOnlyEntry.ean}</div>
                        <div style={{ fontSize: '16px', fontWeight: 700, marginBottom: '4px' }}>{boxOnlyEntry.sku_name}</div>
                        <div style={{ fontSize: '12px', color: C.gray, marginBottom: '14px' }}>SO qty: <b>{refQty(boxOnlyEntry)}</b></div>
                        <div style={{ display: 'flex', gap: '8px', marginBottom: '8px' }}>
                            <div style={{ flex: 1 }}>
                                <div style={{ fontSize: '10.5px', color: C.gray, fontWeight: 700, marginBottom: '3px' }}>Qty per box</div>
                                <input autoFocus type="number" value={boxOnlyPerBox} onChange={e => { setBoxOnlyPerBox(e.target.value); setBoxOnlyError(''); }}
                                    style={{ width: '100%', padding: '11px', border: `2px solid ${C.ink}`, borderRadius: '8px', fontSize: '20px', fontWeight: 800, textAlign: 'center', outline: 'none' }} placeholder="0" />
                            </div>
                            <div style={{ flex: 1 }}>
                                <div style={{ fontSize: '10.5px', color: C.gray, fontWeight: 700, marginBottom: '3px' }}>Number of boxes</div>
                                <input type="number" value={boxOnlyNumBoxes} onChange={e => { setBoxOnlyNumBoxes(e.target.value); setBoxOnlyError(''); }}
                                    onKeyDown={e => e.key === 'Enter' && attemptSaveBoxOnly()}
                                    style={{ width: '100%', padding: '11px', border: `2px solid ${C.ink}`, borderRadius: '8px', fontSize: '20px', fontWeight: 800, textAlign: 'center', outline: 'none' }} placeholder="0" />
                            </div>
                        </div>
                        {(() => {
                            const perBox = parseInt(boxOnlyPerBox, 10), numBoxes = parseInt(boxOnlyNumBoxes, 10);
                            if (isNaN(perBox) || perBox <= 0 || isNaN(numBoxes) || numBoxes <= 0) return null;
                            const packedQty = perBox * numBoxes;
                            const mismatch = packedQty !== refQty(boxOnlyEntry);
                            return <div style={{ fontSize: '13px', fontWeight: 700, color: mismatch ? C.red : C.green, marginBottom: '10px' }}>= {packedQty} units {mismatch && `(SO qty ${refQty(boxOnlyEntry)}, diff ${packedQty - refQty(boxOnlyEntry) > 0 ? '+' : ''}${packedQty - refQty(boxOnlyEntry)})`}</div>;
                        })()}
                        {boxOnlyError && <div style={{ fontSize: '12px', fontWeight: 700, color: C.red, marginBottom: '10px' }}>⚠ {boxOnlyError}</div>}
                        <div style={{ display: 'flex', gap: '10px', marginTop: '8px' }}>
                            <button onClick={closeBoxOnlyEntry} style={{ flex: 1, padding: '12px', background: '#eee', border: 'none', borderRadius: '8px', fontWeight: 700, cursor: 'pointer' }}>Cancel</button>
                            <button onClick={attemptSaveBoxOnly} disabled={boxOnlyBusy} style={{ flex: 2, padding: '14px', background: C.ink, color: '#fff', border: 'none', borderRadius: '8px', fontWeight: 700, cursor: 'pointer' }}>{boxOnlyBusy ? 'Saving…' : 'Save'}</button>
                        </div>
                    </div>
                </div>
            )}
            {boxOnlyConfirm && (
                <div style={S.overlay}>
                    <div style={S.modal}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '10px' }}>
                            <span style={{ fontSize: '20px' }}>⚠️</span><div style={{ fontSize: '16px', fontWeight: 800 }}>Qty doesn't match SO</div>
                        </div>
                        <div style={{ fontSize: '13px', color: C.inkSoft, marginBottom: '16px', lineHeight: 1.5 }}>
                            Packed <b>{boxOnlyConfirm.packedQty}</b>, SO qty is <b>{refQty(boxOnlyEntry)}</b> ({boxOnlyConfirm.diff > 0 ? '+' : ''}{boxOnlyConfirm.diff}). Save anyway?
                        </div>
                        <div style={{ display: 'flex', gap: '10px' }}>
                            <button onClick={() => setBoxOnlyConfirm(null)} style={{ flex: 1, padding: '13px', background: '#eee', border: 'none', borderRadius: '8px', fontWeight: 700, cursor: 'pointer' }}>Cancel</button>
                            <button onClick={() => commitBoxOnly(boxOnlyConfirm.perBox, boxOnlyConfirm.numBoxes)} disabled={boxOnlyBusy}
                                style={{ flex: 2, padding: '14px', background: C.red, color: '#fff', border: 'none', borderRadius: '8px', fontWeight: 700, cursor: 'pointer' }}>{boxOnlyBusy ? 'Saving…' : 'Save anyway'}</button>
                        </div>
                    </div>
                </div>
            )}

            {/* ── Batch label: prompt to take photo (Type-3) ── */}
            {batchLine && !camera && !batchConfirm && (
                <div style={S.overlay}>
                    <div style={S.modal}>
                        <div style={{ fontSize: '12px', color: C.gray, fontFamily: 'monospace', marginBottom: '4px' }}>{batchLine.ean}</div>
                        <div style={{ fontSize: '16px', fontWeight: 700, marginBottom: '4px' }}>{batchLine.sku_name}</div>
                        <div style={{ fontSize: '12px', color: C.gray, marginBottom: '16px' }}>Take a photo of its batch/mfg/exp label. Read automatically.</div>
                        {batchError && <div style={{ color: C.red, fontSize: '12px', fontWeight: 700, marginBottom: '10px' }}>⚠ {batchError}</div>}
                        {batchExtracting ? (
                            <div style={{ textAlign: 'center', padding: '16px', fontSize: '13px', color: C.gray }}>Reading label…</div>
                        ) : (
                            <div style={{ display: 'flex', gap: '10px' }}>
                                <button onClick={() => setBatchLine(null)} style={{ flex: 1, padding: '13px', background: '#eee', border: 'none', borderRadius: '8px', fontWeight: 700, cursor: 'pointer' }}>Cancel</button>
                                <button onClick={() => openCamera('batch')} style={{ flex: 2, padding: '14px', background: C.red, color: '#fff', border: 'none', borderRadius: '8px', fontWeight: 700, cursor: 'pointer' }}>📷 Take label photo</button>
                            </div>
                        )}
                    </div>
                </div>
            )}

            {batchConfirm && (
                <div style={S.overlay}>
                    <div style={S.modal}>
                        <div style={{ fontSize: '16px', fontWeight: 800, marginBottom: '4px' }}>Confirm label details</div>
                        <div style={{ fontSize: '12px', color: C.gray, marginBottom: '10px' }}>Read from the photo — edit only if something's wrong.</div>
                        {batchDiag && (
                            <div style={{ fontSize: '10.5px', color: C.gray, background: C.panel, border: `1px solid ${C.line}`, borderRadius: '6px', padding: '6px 9px', marginBottom: '14px' }}>
                                📷 captured {batchDiag.sourceWidth}×{batchDiag.sourceHeight} → cropped {batchDiag.cropWidth}×{batchDiag.cropHeight} ({batchDiag.via})
                            </div>
                        )}
                        <div style={{ marginBottom: '12px' }}>
                            <div style={{ fontSize: '11.5px', fontWeight: 700, marginBottom: '4px' }}>Batch No.</div>
                            <input value={batchConfirm.batch_no || ''} onChange={e => setBatchConfirm(prev => ({ ...prev, batch_no: e.target.value }))}
                                style={{ width: '100%', padding: '11px', border: `2px solid ${C.ink}`, borderRadius: '8px', fontSize: '16px', fontWeight: 700, outline: 'none' }} />
                        </div>
                        <div style={{ marginBottom: '12px' }}><MonthYearField label="Mfg. Month" value={batchConfirm.mfg_date} onChange={v => setBatchConfirm(prev => ({ ...prev, mfg_date: v }))} /></div>
                        <div style={{ marginBottom: '12px' }}><MonthYearField label="Exp. Month" value={batchConfirm.exp_date} onChange={v => setBatchConfirm(prev => ({ ...prev, exp_date: v }))} /></div>
                        <div style={{ display: 'flex', gap: '10px', marginTop: '8px' }}>
                            <button onClick={() => { setBatchConfirm(null); openCamera('batch'); }} style={{ flex: 1, padding: '13px', background: '#eee', border: 'none', borderRadius: '8px', fontWeight: 700, cursor: 'pointer' }}>Retake</button>
                            <button onClick={confirmBatchInfo} style={{ flex: 2, padding: '14px', background: C.ink, color: '#fff', border: 'none', borderRadius: '8px', fontWeight: 700, cursor: 'pointer' }}>Confirm & Continue</button>
                        </div>
                    </div>
                </div>
            )}

            {/* ── Live camera ── */}
            {camera && (
                <div style={{ position: 'fixed', inset: 0, background: '#000', zIndex: 300, display: 'flex', flexDirection: 'column' }}>
                    <div style={{ padding: '14px 18px', color: '#fff', fontSize: '13px', fontWeight: 700, textAlign: 'center', background: '#111' }}>
                        {camera.mode === 'gallery' ? 'Box photo(s) — tap to shoot, Done when finished' : 'Batch / mfg / exp label'}
                    </div>
                    <div style={{ flex: 1, minHeight: 0, overflow: 'hidden', position: 'relative', background: '#000' }}>
                        <video ref={attachVideo} autoPlay playsInline muted
                            onLoadedMetadata={e => { if (camera.mode !== 'batch') return; const v = e.currentTarget; setPreviewZoom(adaptiveZoomFor({ width: v.videoWidth, height: v.videoHeight }, BATCH_LABEL_TARGET_LONG_EDGE, BATCH_LABEL_MAX_ZOOM)); }}
                            style={{ width: '100%', height: '100%', objectFit: 'contain', transform: camera.mode === 'batch' ? `scale(${previewZoom})` : 'none', transformOrigin: 'center' }} />
                    </div>
                    {camera.mode === 'gallery' && (
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '28px', padding: '20px', background: '#111' }}>
                            <button onClick={closeCamera} style={{ background: '#444', color: '#fff', border: 'none', borderRadius: '10px', padding: '12px 20px', fontWeight: 700, cursor: 'pointer' }}>Done</button>
                            <button onClick={captureSoImage} style={{ width: '68px', height: '68px', borderRadius: '50%', background: C.red, border: '3px solid #fff', cursor: 'pointer' }} />
                            <div style={{ width: '68px', textAlign: 'center', color: '#fff', fontSize: '12px', fontWeight: 700 }}>{soImages.length} shot{soImages.length === 1 ? '' : 's'}</div>
                        </div>
                    )}
                    {camera.mode === 'batch' && (
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '28px', padding: '20px', background: '#111' }}>
                            <button onClick={closeCamera} style={{ background: '#444', color: '#fff', border: 'none', borderRadius: '10px', padding: '12px 20px', fontWeight: 700, cursor: 'pointer' }}>Cancel</button>
                            <button onClick={captureBatchLabel} style={{ width: '68px', height: '68px', borderRadius: '50%', background: C.red, border: '3px solid #fff', cursor: 'pointer' }} />
                            <div style={{ width: '68px' }} />
                        </div>
                    )}
                </div>
            )}

            {/* ── Full box-image gallery modal ── */}
            {galleryModalOpen && (
                <div style={S.overlay} onClick={() => setGalleryModalOpen(false)}>
                    <div style={S.modal} onClick={e => e.stopPropagation()}>
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '12px' }}>
                            <div style={{ fontSize: '16px', fontWeight: 800 }}>All box images ({soImages.length})</div>
                            <button onClick={() => setGalleryModalOpen(false)} style={{ background: 'none', border: 'none', fontSize: '18px', cursor: 'pointer' }}>✕</button>
                        </div>
                        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '8px' }}>
                            {soImages.map((img, idx) => (
                                <div key={img.id ?? img.tempId ?? idx} style={{ position: 'relative', aspectRatio: '1' }}>
                                    <img src={img.dataUrl} alt={`box-${idx}`} onClick={() => setExpandedImage(img)} style={{ width: '100%', height: '100%', objectFit: 'cover', borderRadius: '7px', border: `1.5px solid ${C.ink}`, opacity: img.uploading ? 0.5 : 1, cursor: 'pointer' }} />
                                    <button onClick={() => removeSoImage(idx)} style={{ position: 'absolute', top: '-6px', right: '-6px', background: C.red, color: '#fff', border: 'none', borderRadius: '50%', width: '18px', height: '18px', fontSize: '11px', cursor: 'pointer', lineHeight: '18px', textAlign: 'center', padding: 0 }}>✕</button>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            )}

            {/* ── Single expanded image lightbox ── */}
            {expandedImage && (
                <div style={{ ...S.overlay, alignItems: 'center' }} onClick={() => setExpandedImage(null)}>
                    <img src={expandedImage.dataUrl} alt="expanded" onClick={e => e.stopPropagation()}
                        style={{ maxWidth: '92vw', maxHeight: '88vh', objectFit: 'contain', borderRadius: '8px' }} />
                    <button onClick={() => setExpandedImage(null)}
                        style={{ position: 'absolute', top: '20px', right: '20px', background: 'rgba(0,0,0,0.5)', color: '#fff', border: 'none', borderRadius: '50%', width: '36px', height: '36px', fontSize: '18px', cursor: 'pointer' }}>✕</button>
                </div>
            )}
        </div>
    );
}

// Shared box-summary line for the Final Check review table (box_only:
// range + qty; box_batch: same shape, computed from live `boxes`).
function boxOnlySummaryOrRangeFor(line, boxes) {
    const existing = boxes.filter(b => b.sealed && b.items.some(it => it.line_id === line.id));
    if (!existing.length) return null;
    const numbers = existing.map(b => b.number).sort((a, b) => a - b);
    const qty = existing.reduce((s, b) => s + Number(b.items.find(it => it.line_id === line.id)?.qty || 0), 0);
    const first = numbers[0], last = numbers[numbers.length - 1];
    return `${existing.length} box${existing.length === 1 ? '' : 'es'} · Box ${first}${existing.length > 1 ? ` to ${last}` : ''} · ${qty} units`;
}
